import React from 'react';
import { MessageSquare, Users, Star, Check } from 'lucide-react';
import { useLanguage } from '../../../language/hooks/useLanguageHook';

// Helper function to get feature lists, kept local as it uses the hook
const getFeaturesList = (t, cardKey) => {
  const features = t(`public.landing.features.cards.${cardKey}.features`, { returnObjects: true });
  // Ensure it returns an array even if translation is missing or not an array
  return Array.isArray(features) ? features : [];
};

const FeaturesSection = () => {
  const { t } = useLanguage();

  // Generate feature card data within the component to use the `t` hook
  const featureCards = [
    {
      icon: MessageSquare,
      title: t("public.landing.features.cards.responses.title"),
      description: t("public.landing.features.cards.responses.description"),
      highlight: t("public.landing.features.cards.responses.highlight"),
      features: getFeaturesList(t, "responses")
    },
    {
      icon: Users,
      title: t("public.landing.features.cards.performance.title"),
      description: t("public.landing.features.cards.performance.description"),
      highlight: t("public.landing.features.cards.performance.highlight"),
      features: getFeaturesList(t, "performance")
    },
    {
      icon: Star,
      title: t("public.landing.features.cards.intelligence.title"),
      description: t("public.landing.features.cards.intelligence.description"),
      highlight: t("public.landing.features.cards.intelligence.highlight"),
      features: getFeaturesList(t, "intelligence")
    }
  ];

  return (
    <div className="relative z-10 bg-gray-50"> {/* Ensure z-index if needed */}
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-16 scroll-fade-in">
          <h2 className="text-4xl font-bold mb-4">
            {t("public.landing.features.title")}
          </h2>
          <p className="text-xl text-gray-600">
            {t("public.landing.features.subtitle")}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-12">
          {featureCards.map((feature, index) => (
            <div
              key={feature.title} // Use title as key assuming it's unique
              className="feature-card bg-white rounded-xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 ease-in-out"
              style={{ animationDelay: `${index * 0.1}s` }} // Keep inline style for dynamic delay
            >
              <div className="bg-blue-50 w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 ease-in-out group-hover:scale-110">
                <feature.icon className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
              <p className="text-gray-600 mb-4">{feature.description}</p>
              <div className="bg-green-50 text-green-700 text-sm font-medium px-4 py-2 rounded-full inline-block mb-6">
                {feature.highlight}
              </div>
              <ul className="space-y-3">
                {feature.features.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start"> {/* Use index for list keys */}
                    <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
         {/* Style tag for feature-card animation is handled globally in LandingPage.js */}
      </div>
    </div>
  );
};

export default FeaturesSection; 